import { AppHTTPService } from '@/api/http-service'
import { sendConversationalMessage } from '@/api/sse/prepare-chat/sse.prepare-chat'
import { useCollection } from '@/resources/support/getCollection'
import { Environment } from '@flatfile/api'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { ArrowUp, Clipboard, File, LucideIcon, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../../components/Header/Header'
import {
  FileUpload,
  FileUploads,
  useFileUploadAndExtract,
} from '../../hooks/useFileUploadAndExtract'

interface AttachmentData {
  id: string
  name: string
  type: string
  size: number
}

interface PastedContentData {
  id: string
  content: string
  timestamp: number
}

interface AttachmentPillProps {
  attachment: AttachmentData
  onRemove: (id: string) => void
}

function AttachmentPill({ attachment, onRemove }: AttachmentPillProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  return (
    <div className='flex items-center gap-1 bg-[#E8E8E8] rounded-full px-2 py-1 text-xs'>
      <File className='w-3 h-3 text-[#666666]' />
      <span className='text-[#333333] max-w-[120px] truncate'>
        {attachment.name}
      </span>
      <span className='text-[#666666]'>
        ({formatFileSize(attachment.size)})
      </span>
      <button
        onClick={() => onRemove(attachment.id)}
        className='ml-1 hover:bg-[#DDDDDD] rounded-full p-0.5 transition-colors'
      >
        <X className='w-3 h-3 text-[#666666]' />
      </button>
    </div>
  )
}

interface PastedContentPillProps {
  pastedContent: PastedContentData
  onRemove: (id: string) => void
}

function PastedContentPill({
  pastedContent,
  onRemove,
}: PastedContentPillProps) {
  const truncatedContent =
    pastedContent.content.length > 30
      ? pastedContent.content.substring(0, 30) + '...'
      : pastedContent.content

  return (
    <div className='flex items-center gap-1 bg-[#E8F4FD] rounded-lg px-2 py-1 text-xs border border-[#007AFF20]'>
      <Clipboard className='w-3 h-3 text-[#007AFF]' />
      <span className='text-[#007AFF] max-w-[120px] truncate font-mono'>
        {truncatedContent}
      </span>
      <button
        onClick={() => onRemove(pastedContent.id)}
        className='ml-1 hover:bg-[#007AFF20] rounded-full p-0.5 transition-colors'
      >
        <X className='w-3 h-3 text-[#007AFF]' />
      </button>
    </div>
  )
}

interface PromptTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  attachments?: AttachmentData[]
  pastedContent?: PastedContentData[]
  onRemoveAttachment?: (id: string) => void
  onRemovePastedContent?: (id: string) => void
  onSubmit?: () => void
  isSubmitDisabled?: boolean
  onFileDrop?: (files: File[]) => void
  onPaste?: (content: string) => void
}

function PromptTextarea({
  value,
  onChange,
  placeholder = 'Describe what you need to get done…',
  className = '',
  attachments = [],
  pastedContent = [],
  onRemoveAttachment,
  onRemovePastedContent,
  onSubmit,
  isSubmitDisabled = false,
  onFileDrop,
  onPaste,
}: PromptTextareaProps) {
  const [isDragOver, setIsDragOver] = useState(false)

  const editor = useEditor({
    onUpdate: ({ editor }) => {
      onChange(editor.getText())
    },
    content: value,
    extensions: [
      StarterKit,
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:text-[14px] before:float-left before:text-[#0000004D] before:h-0 before:pointer-events-none',
        placeholder,
      }),
    ],
    editorProps: {
      attributes: {
        class: `pb-5 outline-none  ${className}`,
      },
      handleKeyDown: (view, event) => {
        // Handle Backspace when textarea is empty to delete last attachment
        if (event.key === 'Backspace') {
          const isEmpty = view.state.doc.textContent.trim() === ''
          if (isEmpty) {
            // Remove last pasted content first, then attachments
            if (pastedContent.length > 0 && onRemovePastedContent) {
              event.preventDefault()
              const lastPastedContent = pastedContent[pastedContent.length - 1]
              onRemovePastedContent(lastPastedContent.id)
              return true
            } else if (attachments.length > 0 && onRemoveAttachment) {
              event.preventDefault()
              const lastAttachment = attachments[attachments.length - 1]
              onRemoveAttachment(lastAttachment.id)
              return true
            }
          }
        }

        // Handle Enter key for submission
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          if (onSubmit && !isSubmitDisabled) {
            onSubmit()
          }
          return true
        }
        // Shift+Enter creates a new line (default behavior)
        return false
      },
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData
        if (clipboardData) {
          const pastedText = clipboardData.getData('text/plain')
          if (pastedText.trim() && onPaste) {
            onPaste(pastedText.trim())
            // Prevent the default paste behavior to avoid duplication
            event.preventDefault()
            return true
          }
        }
        return false
      },
    },
  })

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files)
    const acceptedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]

    const validFiles = files.filter((file) => {
      return (
        acceptedTypes.includes(file.type) ||
        file.name.toLowerCase().endsWith('.csv') ||
        file.name.toLowerCase().endsWith('.xls') ||
        file.name.toLowerCase().endsWith('.xlsx')
      )
    })

    if (validFiles.length > 0 && onFileDrop) {
      onFileDrop(validFiles)
    }
  }

  const hasBottomContent = attachments.length > 0 || pastedContent.length > 0

  return (
    <div
      className={`w-full relative w-full rounded-[16px] bg-[#F2F2F2] p-[20px] min-h-[96px] focus:outline-none transition-colors ${
        isDragOver ? 'bg-[#E8F4FD] border-2 border-dashed border-[#007AFF]' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <EditorContent editor={editor} />
      {isDragOver && (
        <div className='absolute inset-0 bg-[#007AFF08] rounded-[16px] flex items-center justify-center pointer-events-none'>
          <div className='text-[#007AFF] text-sm font-medium'>
            Drop your file here
          </div>
        </div>
      )}
      {hasBottomContent && (
        <div className='relative  flex flex-wrap gap-2 max-w-[calc(100%-80px)]'>
          {attachments.map((attachment) => (
            <AttachmentPill
              key={attachment.id}
              attachment={attachment}
              onRemove={onRemoveAttachment || (() => {})}
            />
          ))}
          {pastedContent.map((content) => (
            <PastedContentPill
              key={content.id}
              pastedContent={content}
              onRemove={onRemovePastedContent || (() => {})}
            />
          ))}
        </div>
      )}
      {onSubmit && (
        <button
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          className={`absolute bottom-3 px-[14px] py-[6px] right-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:ring-opacity-50 ${
            isSubmitDisabled
              ? 'bg-[#00000033] cursor-not-allowed'
              : 'bg-[#000000CC] hover:bg-[#000000E6]'
          }`}
        >
          <ArrowUp
            className={`w-4 h-4 ${isSubmitDisabled ? 'text-[#ffffff66]' : 'text-[#fff]'}`}
          />
        </button>
      )}
    </div>
  )
}

interface ActionButtonProps {
  icon: LucideIcon
  label: string
  onClick?: () => void
  disabled?: boolean
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled = false,
}: ActionButtonProps) {
  return (
    <button
      className='flex items-center gap-2 border-[1px] border-[#0000000A] px-[12px] py-[4px] rounded-full hover:bg-[#0000000A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none shadow-[0_0.5px_1px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.04)]'
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className='text-[#00000066] w-[12px] h-[12px]' />
      <span className='text-[#000000B8] text-[12px]'>{label}</span>
    </button>
  )
}

export default function UploadView() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [attachments, setAttachments] = useState<AttachmentData[]>([])
  const [pastedContent, setPastedContent] = useState<PastedContentData[]>([])

  const environments = useCollection('ENVIRONMENTS')
  const environmentId = (environments.data[0] as Environment)?.id ?? ''

  const { threadId: initialThreadId } = useParams<{
    threadId: string
  }>()
  const [prompt, setPrompt] = useState('')
  const navigate = useNavigate()
  const [sessionSpaceId, setSessionSpaceId] = useState()
  const [threadId, setThreadId] = useState(initialThreadId)
  const [processedFiles, setProcessedFiles] = useState<FileUploads>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { handleFileUpload } = useFileUploadAndExtract({
    environmentId: environmentId || '',
    spaceId: sessionSpaceId || '',
    onUploadError: (error) => {
      console.error(error)
    },
    onExtractionComplete: (addedFileUploads: { [id: string]: FileUpload }) => {
      setProcessedFiles((prevFiles) => ({
        ...prevFiles,
        ...addedFileUploads,
      }))
    },
    onExtractionError: (error) => {
      console.error('Extraction error:', error)
    },
    onUnsupportedFile: () => {
      console.error('Unsupported file format')
    },
  })

  const handleUpload = async (droppedFiles: File[]) => {
    // Add files as attachments
    const newAttachments = droppedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      type: file.type,
      size: file.size,
    }))
    setAttachments((prev) => [...prev, ...newAttachments])

    // Validate we have files to upload
    if (droppedFiles.length === 0) {
      console.error('No files to upload')
      return
    }

    try {
      // First, ensure we have a session (create one if needed)
      let currentSessionSpaceId

      if (threadId) {
        const thread = await AppHTTPService.client.threads.get(threadId)
        currentSessionSpaceId = thread.data.projectId
      } else {
        // Use the file name as the session name for better identification
        const fileName = droppedFiles[0].name
        // Remove file extension for cleaner session name
        const sessionName = fileName.includes('.')
          ? fileName.substring(0, fileName.lastIndexOf('.'))
          : fileName

        const newSession =
          await AppHTTPService.client.projects.create(sessionName)
        // Access the session ID from the response
        // The API returns a space object where the ID is in the data property
        currentSessionSpaceId = newSession.id
        setSessionSpaceId(currentSessionSpaceId)

        // Create a new thread

        const thread = await AppHTTPService.client.threads.create(
          currentSessionSpaceId,
          {
            description: 'handleUpload() Thread',
          },
          currentSessionSpaceId,
          'chat'
        )

        setThreadId(thread.id)

        // Update the URL with the sessionSpaceId parameter without reloading the page
        navigate(`/projects/${currentSessionSpaceId}/upload/`, {
          replace: true,
        })
      }

      // Now that we have a session, upload the file
      await handleFileUpload(droppedFiles[0], currentSessionSpaceId)
    } catch (error) {
      console.error('Error in upload process:', error)
    }
  }

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
  }

  const handleRemovePastedContent = (id: string) => {
    setPastedContent((prev) => prev.filter((content) => content.id !== id))
  }

  const handlePaste = (content: string) => {
    const newPastedContent: PastedContentData = {
      id: `paste-${Date.now()}`,
      content,
      timestamp: Date.now(),
    }
    setPastedContent((prev) => [...prev, newPastedContent])
  }

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files
    if (selectedFiles && selectedFiles.length > 0) {
      handleUpload(Array.from(selectedFiles))
    }
    // Reset the input so the same file can be selected again
    event.target.value = ''
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const handleSubmit = useCallback(async () => {
    if (sessionSpaceId && Object.keys(processedFiles).length > 0 && threadId) {
      setIsSubmitting(true)
      try {
        // Combine prompt with pasted content
        let fullPrompt = prompt.trim()
        if (pastedContent.length > 0) {
          const pastedText = pastedContent
            .map((content) => content.content)
            .join('\n\n')
          fullPrompt = fullPrompt
            ? `${fullPrompt}\n\n${pastedText}`
            : pastedText
        }

        // Navigate to the prepare chat page immediately
        navigate(`/projects/${sessionSpaceId}/prepare-chat/`)

        // Only send the message if there's a prompt
        if (fullPrompt) {
          const fileId = Object.keys(processedFiles)[0]
          const sheetsResponse = await AppHTTPService.client.sheets.list(fileId)
          const sourceSheetId = sheetsResponse.data[0]?.id

          await AppHTTPService.client.messages.create(threadId, {
            content: { text: fullPrompt },
            type: 'text',
            role: 'user',
            metadata: {},
          })

          await sendConversationalMessage(threadId, fullPrompt, sourceSheetId)
        }
      } catch (error) {
        console.error('Error creating message:', error)
      } finally {
        setIsSubmitting(false)
      }
    } else if (sessionSpaceId && Object.keys(processedFiles).length > 0) {
      // No prompt provided, just navigate
      navigate(`/projects/${sessionSpaceId}/prepare-chat/`)
    }
  }, [
    prompt,
    pastedContent,
    sessionSpaceId,
    processedFiles,
    threadId,
    navigate,
  ])

  const lastUsedProjectId = localStorage.getItem('lastUsedProjectId')

  return (
    <div className='flex flex-col min-h-screen bg-white text-black font-sans'>
      <input
        ref={fileInputRef}
        type='file'
        accept='.csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        multiple={false}
      />
      {lastUsedProjectId && <Header isUploading />}
      <main className='flex-1 flex justify-center items-center p-6 md:p-8 relative'>
        <div className='max-w-[600px] w-full flex flex-col items-center relative z-1'>
          <h1 className='text-[18px] font-medium mb-8 text-center text-[#242424'>
            Get your sheet together
          </h1>

          <div className='w-full flex flex-col items-center gap-[12px]'>
            <PromptTextarea
              value={prompt}
              onChange={setPrompt}
              placeholder='Describe what you need to get done…'
              attachments={attachments}
              pastedContent={pastedContent}
              onRemoveAttachment={handleRemoveAttachment}
              onRemovePastedContent={handleRemovePastedContent}
              onSubmit={handleSubmit}
              isSubmitDisabled={
                !sessionSpaceId ||
                Object.keys(processedFiles).length === 0 ||
                !threadId ||
                isSubmitting
              }
              onFileDrop={handleUpload}
              onPaste={handlePaste}
            />

            <div className='flex flex-wrap gap-2 justify-center'>
              <ActionButton
                icon={File}
                label='Upload file'
                onClick={triggerFileInput}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
